/**
 * Pasting a connection string in the New Connection dialog (Option C, C3; plan §3.2 / C3). The parser is reused, the
 * old apply-on-change field is not: nothing reaches the form before a deliberate Continue (the start view's detection
 * card) or Apply (the form view's paste field), per D16.
 *
 * - `apply(result, source, previousEngine)` — Continue / Apply: the string's patch goes through the draft contract
 *   (`applyPatch`: another engine's draft is restored or seeded first, the one on screen is kept; the same engine is
 *   patched in place), the pane's summary line is set, and `feature:connection_string_applied` fires.
 * - The paste field (form view): `openField()` from the rail's or Add from…'s entry (a second use focuses it again),
 *   `closeField(via)` from its close button or Escape (`ui:connection_paste_field_closed`), or silently. Its text lives
 *   here, so text waiting for Apply counts as input (`hasPendingText`) when a nested flow completes (C3 review C3-04).
 * - A command-palette clipboard or SAS prefill handoff (the one this dialog was opened for) is consumed before the first
 *   paint and applied as it is — it already showed its preview — with the summary line saying so. A clipboard handoff
 *   without an engine becomes `startPrefill` (ruling after C3 review r6): its text for the start filter (a string that
 *   asks which engine, or one the rules refused), or only the paste hint.
 */
import { useLayoutEffect, useRef, useState } from 'react';
import type { TrackFunction } from '@/core/analytics/analyticsTypes';
import { parseResultPatch } from '../../services/connection-string/applyParseResultToForm';
import type { ConnectionStringParseResult } from '../../services/connection-string/parseConnectionString';
import type { DatabaseEngine } from '../../components/connectionFormSchema';
import { useConnectionStringPrefillStore } from '../../store/connectionStringPrefillStore';
import { useConnectionsUIStore } from '../../store/uiStore';
import type { useEngineDrafts } from '../state/useEngineDrafts';
import { appliedPaste, buildPastePreview, type AppliedPaste, type PastePreview } from '../paste/pastePreview';
import type { PasteSource } from '../paste/usePasteTracking';
import { FUNNEL_VERSION } from './useConnectionDialogFunnel';

/** The engine of the pending prefill handoff a dialog is opened for, if any (it is consumed before the first paint). */
export function pendingPrefillEngine(handoffId: string | null): DatabaseEngine | undefined {
  const pending = useConnectionStringPrefillStore.getState().pending;
  return handoffId && pending?.handoffId === handoffId ? pending.result.engine ?? undefined : undefined;
}

type Drafts = Pick<ReturnType<typeof useEngineDrafts>, 'applyPatch' | 'baseValuesFor'>;
type Options = { drafts: Drafts; isEditMode: boolean; prefillHandoffId: string | null; track: TrackFunction };

export function usePasteEntry({ drafts, isEditMode, prefillHandoffId, track }: Options) {
  const [open, setOpen] = useState(false);
  // Moves on every use of the Paste entry, so a second use focuses the open field again (0: the field was not asked for).
  const [focusRequest, setFocusRequest] = useState(0);
  const [applied, setApplied] = useState<AppliedPaste | null>(null);
  const [startPrefill, setStartPrefill] = useState<{ text: string } | { hint: true } | null>(null);
  // The paste field's text (it is the field's value; kept here so pending text counts as input), and whether it was
  // pasted: a pasted string shows as a chip, never as text (final UX review UX-03).
  const [text, setTextState] = useState('');
  const [textVia, setTextVia] = useState<'type' | 'paste'>('type');
  const textRef = useRef('');
  const setText = (next: string, via: 'type' | 'paste' = 'type') => {
    textRef.current = next;
    setTextState(next);
    setTextVia(via);
  };

  /** The string's preview for the draft it would land on (null: nothing to apply). */
  const previewFor = (result: ConnectionStringParseResult): PastePreview | null => {
    const parsed = parseResultPatch(result);
    return parsed ? buildPastePreview(result, drafts.baseValuesFor(parsed.engine)) : null;
  };

  const patch = (result: ConnectionStringParseResult, via: AppliedPaste['via']): PastePreview | null => {
    const parsed = parseResultPatch(result);
    const preview = previewFor(result);
    if (!parsed || !preview) return null;
    drafts.applyPatch(parsed.engine, parsed.patch, via === 'prefill' ? 'prefill' : 'paste');
    setApplied(appliedPaste(preview, via));
    return preview;
  };

  // Before the first paint: the dialog opens in the prefilled engine's form, never a frame of another engine.
  useLayoutEffect(() => {
    if (isEditMode || !prefillHandoffId) return;
    const pending = useConnectionStringPrefillStore.getState().consume(prefillHandoffId);
    if (pending?.result.engine) patch(pending.result, 'prefill');
    else if (pending) setStartPrefill(pending.input ? { text: pending.input } : { hint: true });
    useConnectionsUIStore.getState().acknowledgePrefillHandoff(prefillHandoffId);
    // Consume only the handoff this dialog was opened for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillHandoffId]);

  /** Continue (start view) or Apply (paste field): the engine the string filled, or null when it names none. */
  const apply = (result: ConnectionStringParseResult, source: PasteSource, previousEngine: DatabaseEngine | 'none'): DatabaseEngine | null => {
    const preview = patch(result, 'pasted');
    if (!preview) return null;
    track('feature:connection_string_applied', { source, changed_engine: preview.engine !== previousEngine, funnel_version: FUNNEL_VERSION });
    return preview.engine;
  };

  return {
    open,
    focusRequest,
    applied,
    text,
    textVia,
    setText,
    /** Text waiting in the open paste field for Apply (read fresh). */
    hasPendingText: () => textRef.current.trim() !== '',
    previewFor,
    apply,
    /** A palette handoff for the start view (set once, before the first paint). */
    startPrefill,
    openField: () => {
      setOpen(true);
      setFocusRequest((request) => request + 1);
    },
    /** `via` for the user's own close (button or Escape); none when the dialog closes it (Apply, All engines, a save). */
    closeField: (via?: 'button' | 'escape') => {
      setOpen(false);
      setText('');
      if (via) track('ui:connection_paste_field_closed', { via, funnel_version: FUNNEL_VERSION });
    },
    /** A Browse Azure pick filled the form: the pane's summary line says so (final UX review UX-01). */
    noteApplied: (summary: AppliedPaste) => setApplied(summary),
    /** A save that keeps the dialog open starts over: no summary of the saved draft remains. */
    reset: () => {
      setOpen(false);
      setText('');
      setApplied(null);
    },
  };
}

export type PasteEntry = ReturnType<typeof usePasteEntry>;
